import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import "../styles/global.css";

/* ─────────────────────────────────────────
   Inline styles — uses your CSS variables
───────────────────────────────────────── */
const S = {
  /* hero */
  hero: {
    position: "relative",
    overflow: "hidden",
    background: "#2F4A3A",
    padding: "100px 0 80px",
    color: "#fff",
  },
  heroBubble: (size, top, left, opacity) => ({
    position: "absolute",
    width: size,
    height: size,
    borderRadius: "50%",
    background: "rgba(255,255,255," + opacity + ")",
    top,
    left,
    pointerEvents: "none",
  }),
  heroTag: {
    display: "inline-block",
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.35)",
    borderRadius: "50px",
    padding: "6px 20px",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "2px",
    textTransform: "uppercase",
    marginBottom: "18px",
    color: "#fff",
  },
  heroTitle: {
    fontSize: "clamp(2rem, 2vw, 3.6rem)",
    fontWeight: 800,
    lineHeight: 1.15,
    marginBottom: "20px",
    // fontFamily: "'Playfair Display', Georgia, serif",
  },
  heroSub: {
    fontSize: "1.1rem",
    opacity: 0.88,
    maxWidth: "540px",
    lineHeight: 1.7,
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(255,255,255,0.18)",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: "12px",
    padding: "12px 20px",
    marginTop: "12px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#fff",
  },

  /* section */
  sectionPad: { padding: "80px 0" },
  sectionTitle: {
    fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "12px",
  },
  accent: { color: "#58A186" },
  divider: {
    width: "56px",
    height: "4px",
    borderRadius: "4px",
    background: "linear-gradient(90deg, #58A186, #C38635)",
    margin: "0 0 24px",
  },
  dividerCenter: {
    width: "56px",
    height: "4px",
    borderRadius: "4px",
    background: "linear-gradient(90deg, #58A186, #C38635)",
    margin: "0 auto 24px",
  },
  subText: { color: "#64748b", fontSize: "1rem", lineHeight: 1.75 },

  /* mission / vision cards */
  mvCard: (bg) => ({
    background: bg,
    borderRadius: "20px",
    padding: "36px 32px",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 4px 24px rgba(12,25,41,0.08)",
  }),
  mvIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    marginBottom: "18px",
    background: "rgba(255,255,255,0.25)",
  },
  mvTitle: {
    fontSize: "1.4rem",
    fontWeight: 700,
    marginBottom: "12px",
    color: "#fff",
  },
  mvText: { color: "rgba(255,255,255,0.88)", lineHeight: 1.75, fontSize: "0.97rem" },

  /* stat cards */
  statCard: {
    background: "#fff",
    borderRadius: "20px",
    padding: "36px 24px",
    textAlign: "center",
    boxShadow: "0 2px 20px rgba(12,25,41,0.07)",
    border: "1px solid #eef1f6",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
  },
  statNum: {
    fontSize: "2.8rem",
    fontWeight: 800,
    background: "linear-gradient(135deg, #1E7A8C, #58A186)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    lineHeight: 1,
    marginBottom: "8px",
  },
  statLabel: { color: "#64748b", fontSize: "0.92rem", fontWeight: 500 },

  /* team cards */
  teamCard: {
    background: "#fff",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 2px 20px rgba(12,25,41,0.07)",
    border: "1px solid #eef1f6",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    height: "100%",
    position: "relative", /* required so the stretched-link's ::after can fill this card */
    cursor: "pointer",
  },
  teamAvatar: (bg) => ({
    width: "100%",
    height: "200px",
    background: bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "52px",
    overflow: "hidden",
  }),
  teamInfo: { padding: "20px 22px" },
  teamName: { fontWeight: 700, fontSize: "1.05rem", color: "#0f172a", marginBottom: "4px" },
  teamRole: { color: "#58A186", fontSize: "0.88rem", fontWeight: 600, marginBottom: "10px" },
  teamBio: { color: "#64748b", fontSize: "0.88rem", lineHeight: 1.6 },

  /* team skeleton */
  teamSkelBlock: (h, w = "100%") => ({
    height: h,
    width: w,
    background: "#eef1f6",
    borderRadius: 8,
  }),

  /* values */
  valueCard: {
    background: "#fff",
    borderRadius: "16px",
    padding: "28px 24px",
    boxShadow: "0 2px 16px rgba(12,25,41,0.06)",
    border: "1px solid #eef1f6",
    transition: "transform 0.22s ease",
    height: "100%",
  },
  valueIcon: (bg) => ({
    width: "52px",
    height: "52px",
    borderRadius: "14px",
    background: bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    marginBottom: "16px",
    color: "#0f172a",
  }),
  valueTitle: { fontWeight: 700, fontSize: "1rem", color: "#0f172a", marginBottom: "8px" },
  valueText: { color: "#64748b", fontSize: "0.9rem", lineHeight: 1.65 },

  /* CTA strip */
  ctaStrip: {
    background: "linear-gradient(135deg, #1E7A8C 0%, #167A7A 50%, #238495 100%)",
    borderRadius: "24px",
    padding: "60px 48px",
    color: "#fff",
    position: "relative",
    overflow: "hidden",
  },
  ctaTitle: {
    fontSize: "clamp(1.5rem, 2vw, 2.2rem)",
    fontWeight: 600,
    marginBottom: "12px",
  },
  ctaBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff",
    color: "#1E7A8C",
    fontWeight: 700,
    fontSize: "15px",
    padding: "14px 32px",
    borderRadius: "50px",
    border: "none",
    cursor: "pointer",
    textDecoration: "none",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  },
  ctaBtnOutline: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "transparent",
    color: "#fff",
    fontWeight: 700,
    fontSize: "15px",
    padding: "13px 32px",
    borderRadius: "50px",
    border: "2px solid rgba(255,255,255,0.6)",
    cursor: "pointer",
    textDecoration: "none",
    transition: "all 0.2s ease",
  },

  /* grey bg section */
  greySection: { background: "#f8fafc", padding: "80px 0" },
};

const stats = [
  { num: "25+", label: "Years" },
  { num: "6L+", label: "Patients" },
  { num: "18+", label: "Branches" },
  { num: "450+", label: "Formulas" },
];

/* ─────────────────────────────────────────
   Values — Font Awesome icon classes.
   Requires Font Awesome loaded globally, e.g. in public/index.html:
   <link rel="stylesheet"
     href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css">
───────────────────────────────────────── */
const values = [
  { icon: "fa-solid fa-leaf", bg: "rgba(88,161,134,0.12)", title: "Natural Quality", text: "We source only the finest natural ingredients, ensuring every product meets our strict quality standards before reaching you." },
  { icon: "fa-solid fa-flask", bg: "rgba(30,122,140,0.12)", title: "Science-Backed", text: "Our formulations are grounded in modern research, combining traditional wisdom with cutting-edge science." },
  { icon: "fa-solid fa-handshake", bg: "rgba(195,134,53,0.12)", title: "Customer First", text: "Your wellbeing is our priority. We stand behind every product with honest advice and dedicated support." },
  { icon: "fa-solid fa-globe", bg: "rgba(22,122,122,0.12)", title: "Sustainability", text: "Ethical sourcing, eco-friendly packaging, and responsible practices guide every decision we make." },
  { icon: "fa-solid fa-circle-check", bg: "rgba(88,161,134,0.12)", title: "Transparency", text: "Clear labelling, honest ingredients, no hidden fillers — what you see is exactly what you get." },
  { icon: "fa-solid fa-rocket", bg: "rgba(30,122,140,0.12)", title: "Innovation", text: "We continuously explore new formulations and delivery methods to bring you better health solutions." },
];

export default function AboutUs() {
  const [hovered, setHovered] = useState(null);

  /* ── Our Team — real doctors, driven by a DEDICATED Settings field ──
     Settings.js has its own "Meet Our Team (About Us page)" multi-select
     (field: "team_doctors"), separate from the "Doctors" field the Home
     page uses. api/settingsFront/1 returns data.data.team_doctors — the
     admin can pick/order a completely different set of doctors here
     without affecting the Home page's Doctors section. */
  const [teamDoctors, setTeamDoctors] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/settingsFront/1`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data?.team_doctors)) {
          setTeamDoctors(data.data.team_doctors);
        }
      })
      .catch((err) => console.log("About Us team fetch error:", err))
      .finally(() => setLoadingTeam(false));
  }, []);

  return (
    <>
      <Header />

      {/* ── Breadcrumb ── */}
      <section style={{ padding: "16px 0", background: "#f8fafc", borderBottom: "1px solid #eef1f6" }}>
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
               <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active" aria-current="page">
                About Us
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* ── Hero ── */}
      <section style={S.hero}>
        {/* decorative bubbles */}
        <div style={S.heroBubble("320px", "-80px", "-80px", 0.06)}></div>
        <div style={S.heroBubble("200px", "40px", "60%", 0.05)}></div>
        <div style={S.heroBubble("140px", "auto", "auto", 0.07, "bottom:-40px", "right:-40px")}></div>

        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div style={S.heroTag}>🌿 Our Story</div>
              <h1 style={S.heroTitle}>
                Rooted in Nature,<br />Driven by Science
              </h1>
              <p style={S.heroSub}>
                For over 25 + years Dr.Shreevarma's Wellness integrates traditional Ayurveda with modern medical approaches, offering holistic healing and personalised care for patients through in-clinic and online consultations.
              </p>
              <div className="d-flex flex-wrap gap-3 mt-4">
                <div style={S.heroBadge}><span>🏆</span> Award-winning formulations</div>
                <div style={S.heroBadge}><span>🌿</span> 100% Natural sourcing</div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="row g-3">
                {stats.map((s, i) => (
                  <div className="col-6" key={i}>
                    <div style={{
                      background: "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.22)",
                      borderRadius: "16px",
                      padding: "28px 20px",
                      textAlign: "center",
                      backdropFilter: "blur(8px)",
                    }}>
                      <div style={{ fontSize: "2rem", fontWeight: 800, marginBottom: "6px" }}>{s.num}</div>
                      <div style={{ fontSize: "0.88rem", opacity: 0.85, fontWeight: 500 }}>{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission / Vision ── */}
      <section style={S.sectionPad}>
        <div className="container">
          <div className="text-center mb-5">
            <div style={S.dividerCenter}></div>
            <h2 style={{ ...S.sectionTitle, textAlign: "center" }}>What <span style={S.accent}>Drives</span> Us</h2>
            <p style={{ ...S.subText, maxWidth: "520px", margin: "0 auto" }}>Our mission and vision shape every decision, from ingredient selection to how we serve you.</p>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <div style={S.mvCard("linear-gradient(145deg, #1E7A8C, #238495)")}>
                <div style={S.mvIcon}>🎯</div>
                <div style={S.mvTitle}>Our Mission</div>
                <p style={S.mvText}>To provide accessible, ethical and evidence-based healthcare through prevention, cure ,rehabilitation, education, research and wellness innovation, empowering healthier lives and communities.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div style={S.mvCard("linear-gradient(145deg, #C38635, #a06e28)")}>
                <div style={S.mvIcon}>🔭</div>
                <div style={S.mvTitle}>Our Vision</div>
                <p style={S.mvText}>Becoming the world’s most trusted integrative wellness ecosystem, transforming lives through the harmonious union of traditional wisdom, modern science, and conscious living.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div style={S.mvCard("linear-gradient(145deg, #167A7A, #0d5858)")}>
                <div style={S.mvIcon}>💎</div>
                <div style={S.mvTitle}>Our Promise</div>
                <p style={S.mvText}>Every product we offer is tested, verified, and crafted with integrity. We promise honest labelling, ethical practices, and genuine care for your wellbeing.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Our Story ── */}
      <section style={S.greySection}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div style={S.divider}></div>
              <h2 style={S.sectionTitle}>The <span style={S.accent}>Story</span> Behind the Brand</h2>
              <p style={{ ...S.subText, marginBottom: "16px" }}>
            “At Dr.Shreevarma's Wellness, We Are Dedicated To Helping You Achieve Complete Wellness. We Combine The Wisdom Of Traditional Ayurveda With Modern Healthcare Insights To Create Personalized Treatment Plans. Whether You Seek Relief From Chronic Conditions Or Guidance For A Healthier Lifestyle, Our Clinic Offers A Safe, Supportive, And Caring Environment For Every Patient.”
              </p>
            </div>
            <div className="col-lg-6">
              {/* Visual timeline */}
              <div style={{ position: "relative", paddingLeft: "32px" }}>
                {[
                  { year: "2009", title: "Founded in Chennai", desc: "Started with 5 hand-crafted herbal products from a small facility." },
                  { year: "2013", title: "GMP Certification", desc: "Earned pharmaceutical-grade manufacturing certification." },
                  { year: "2017", title: "10,000 Customers", desc: "Crossed a milestone of 10,000 loyal customers across India." },
                  { year: "2021", title: "Online Launch", desc: "Launched our e-commerce platform, making wellness accessible nationwide." },
                  { year: "2024", title: "500+ Products", desc: "Expanded to over 500 SKUs spanning vitamins, herbs, and superfoods." },
                ].map((item, i) => (
                  <div key={i} style={{ position: "relative", paddingLeft: "24px", paddingBottom: i < 4 ? "28px" : 0 }}>
                    {/* line */}
                    {i < 4 && <div style={{ position: "absolute", left: "-17px", top: "24px", width: "2px", height: "calc(100% - 10px)", background: "linear-gradient(to bottom, #58A186, #eef1f6)" }}></div>}
                    {/* dot */}
                    <div style={{ position: "absolute", left: "-24px", top: "6px", width: "14px", height: "14px", borderRadius: "50%", background: "#58A186", border: "3px solid #fff", boxShadow: "0 0 0 2px #58A186" }}></div>
                    <div style={{ background: "#fff", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", border: "1px solid #eef1f6" }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#58A186", marginBottom: "4px", letterSpacing: "1px" }}>{item.year}</div>
                      <div style={{ fontWeight: 700, fontSize: "0.97rem", color: "#0f172a", marginBottom: "4px" }}>{item.title}</div>
                      <div style={{ fontSize: "0.87rem", color: "#64748b" }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Our Values ── */}
      <section style={S.sectionPad}>
        <div className="container">
          <div className="text-center mb-5">
            <div style={S.dividerCenter}></div>
            <h2 style={{ ...S.sectionTitle, textAlign: "center" }}>Our Core <span style={S.accent}>Values</span></h2>
            <p style={{ ...S.subText, maxWidth: "480px", margin: "0 auto" }}>These principles guide every product we create and every interaction we have.</p>
          </div>
          <div className="row g-4">
            {values.map((v, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <div
                  style={{ ...S.valueCard, transform: hovered === `v${i}` ? "translateY(-4px)" : "none", boxShadow: hovered === `v${i}` ? "0 8px 28px rgba(12,25,41,0.12)" : S.valueCard.boxShadow }}
                  onMouseEnter={() => setHovered(`v${i}`)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div style={S.valueIcon(v.bg)}>
                    <i className={v.icon}></i>
                  </div>
                  <div style={S.valueTitle}>{v.title}</div>
                  <div style={S.valueText}>{v.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Meet Our Team — real doctors from the dedicated "team_doctors"
           setting configured in the admin Settings page (About Us section,
           independent of the Home page's "Doctors" field). However many
           doctors the admin selects and in whatever order they arrange
           them is exactly what renders here. ── */}
      <section style={S.greySection}>
        <div className="container">
          <div className="text-center mb-5">
            <div style={S.dividerCenter}></div>
            <h2 style={{ ...S.sectionTitle, textAlign: "center" }}>Meet Our <span style={S.accent}>Team</span></h2>
            <p style={{ ...S.subText, maxWidth: "520px", margin: "0 auto" }}>
              The doctors and specialists behind every consultation and every formulation.
            </p>
          </div>

          <div className="row g-4">
            {loadingTeam
              ? [...Array(4)].map((_, i) => (
                  <div className="col-6 col-lg-3" key={i}>
                    <div style={S.teamCard}>
                      <div style={S.teamAvatar("#eef1f6")}></div>
                      <div style={S.teamInfo}>
                        <div style={S.teamSkelBlock(14, "70%")}></div>
                        <div style={{ height: 8 }} />
                        <div style={S.teamSkelBlock(11, "50%")}></div>
                        <div style={{ height: 10 }} />
                        <div style={S.teamSkelBlock(11, "90%")}></div>
                      </div>
                    </div>
                  </div>
                ))
              : teamDoctors.map((doctor) => (
                  <div className="col-6 col-lg-3" key={doctor.id}>
                    <div
                      style={{
                        ...S.teamCard,
                        transform: hovered === `t${doctor.id}` ? "translateY(-4px)" : "none",
                        boxShadow: hovered === `t${doctor.id}` ? "0 10px 30px rgba(12,25,41,0.14)" : S.teamCard.boxShadow,
                      }}
                      onMouseEnter={() => setHovered(`t${doctor.id}`)}
                      onMouseLeave={() => setHovered(null)}
                    >
                      <div style={S.teamAvatar("linear-gradient(135deg, rgba(88,161,134,0.2), rgba(30,122,140,0.2))")}>
                        <img
                          src={doctor.images
                            ? `${API_BASE_URL}public/doctorimages/${doctor.images.split(",")[0]}`
                            : `${API_BASE_URL}public/doctorimages/doctor-placeholder.jpg`}
                          onError={(e) => { e.currentTarget.src = `${API_BASE_URL}public/doctorimages/doctor-placeholder.jpg`; }}
                          alt={doctor.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.currentTarget.src = `${API_BASE_URL}public/noimage.jpg`; }}
                        />
                      </div>
                      <div style={S.teamInfo}>
                        <div style={S.teamName}>{doctor.name}</div>
                        <div style={S.teamRole}>{doctor?.department_title?.name || "Wellness Expert"}</div>
                        <div style={S.teamBio}>
                          {doctor?.experience ? `${doctor.experience} experience` : ""}
                          {doctor?.specializations ? ` — ${doctor.specializations}` : ""}
                        </div>
                      </div>

                      {/* Stretched link — makes the ENTIRE card clickable, not just
                          the name/image. Bootstrap's "stretched-link" class puts an
                          absolutely-positioned ::after over the nearest position:relative
                          ancestor (teamCard above), so a click anywhere on the card
                          (avatar, name, bio, empty padding) navigates to the doctor's
                          detail page — same route pattern used on the Home page. */}
                      <Link
                        to={`/doctordetails/${doctor.slug}`}
                        className="stretched-link"
                        aria-label={`View ${doctor.name}'s profile`}
                      />
                    </div>
                  </div>
                ))}
          </div>

          {!loadingTeam && teamDoctors.length === 0 && (
            <p style={{ ...S.subText, textAlign: "center", marginTop: "20px" }}>
              Our team details will appear here once doctors are added in the admin panel.
            </p>
          )}
        </div>
      </section>

      {/* ── CTA Strip ── */}
      <section style={{ padding: "60px 0" }}>
        <div className="container">
          <div style={S.ctaStrip}>
            {/* decorative circle */}
            <div style={{ position: "absolute", width: "300px", height: "300px", borderRadius: "50%", background: "rgba(255,255,255,0.05)", right: "-60px", top: "-80px", pointerEvents: "none" }}></div>
            <div style={{ position: "absolute", width: "180px", height: "180px", borderRadius: "50%", background: "rgba(255,255,255,0.05)", right: "80px", bottom: "-60px", pointerEvents: "none" }}></div>
            <div className="row align-items-center g-4" style={{ position: "relative", zIndex: 1 }}>
              <div className="col-lg-7">
                <div style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", opacity: 0.8, marginBottom: "10px" }}>Start Your Journey</div>
                <h2 style={S.ctaTitle}>Ready to Experience the Difference?</h2>
                <p style={{ opacity: 0.85, fontSize: "1rem", lineHeight: 1.7 }}>Explore our full range of natural wellness products, each crafted with the care and science your health deserves.</p>
              </div>
              <div className="col-lg-5 d-flex gap-3 flex-wrap justify-content-lg-end">
                <Link to="/ayush-product-list" style={S.ctaBtn}>
                 Shop Now
                </Link>
                <Link to="/contact" style={S.ctaBtnOutline}>
                   Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}